import type { TableSort } from '@myrewards/contracts';

// Sorting a work table (APP-149 D3). The page sorts the whole list, then pages it (APP-135), so a
// sort never applies to one page only; DataTable draws the headings and never reorders its rows.

/** The column a table is sorted by, and which way: one shape, the contract's (a saved sort's too). */
export type { TableSort };

export type SortDir = TableSort['dir'];

/** How a column sorts: the value it compares, and what each direction is called. */
export interface ColumnSort<Row> {
  /** What the column compares. Null sorts last whichever way the column runs. */
  value: (row: Row) => string | number | null;
  /**
   * What the status line says for each direction ("oldest first", "newest first"). Left out, a
   * column of words reads "A to Z" / "Z to A" and a column of numbers "lowest first" / "highest
   * first".
   */
  words?: readonly [asc: string, desc: string];
}

/** The parts of a column that sorting reads (a `DataColumn` has them all). */
export interface SortColumn<Row> {
  id: string;
  header: string;
  numeric?: boolean | undefined;
  sort?: ColumnSort<Row> | undefined;
}

const TEXT_WORDS = ['A to Z', 'Z to A'] as const;
const NUMBER_WORDS = ['lowest first', 'highest first'] as const;

// One collator for every table: "PAA-2026-0009" before "PAA-2026-0010", and case doesn't matter.
const collator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'base' });

/** A value to compare, or null for nothing to compare (null, or a number that isn't one). */
function comparable(value: string | number | null): string | number | null {
  return typeof value === 'number' && Number.isNaN(value) ? null : value;
}

function compare(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return collator.compare(String(a), String(b));
}

/**
 * The rows in the table's order, as a new list. Stable; nulls go last in both directions; words
 * compare in natural order (numbers inside them by value, case ignored). Rows that tie are put in
 * `tieBreak` order (the award number, D3), ascending whichever way the column runs, so flipping a
 * column keeps each group's own order. An unknown or unsortable column leaves the order as it is.
 */
export function sortRows<Row>(
  rows: readonly Row[],
  columns: readonly Pick<SortColumn<Row>, 'id' | 'sort'>[],
  sort: TableSort,
  tieBreak?: (row: Row) => string,
): Row[] {
  const by = columns.find((c) => c.id === sort.column)?.sort;
  if (by === undefined) return [...rows];
  const sign = sort.dir === 'asc' ? 1 : -1;
  const keyed = rows.map((row, index) => ({ row, index, value: comparable(by.value(row)) }));
  keyed.sort((a, b) => {
    if (a.value === null || b.value === null) {
      if (a.value !== b.value) return a.value === null ? 1 : -1;
    } else {
      const order = compare(a.value, b.value);
      if (order !== 0) return sign * order;
    }
    if (tieBreak !== undefined) {
      const order = collator.compare(tieBreak(a.row), tieBreak(b.row));
      if (order !== 0) return order;
    }
    return a.index - b.index;
  });
  return keyed.map((k) => k.row);
}

/**
 * What one direction of a column is called: its own words, else by what it holds (the first value
 * there is; with none, a numeric column's numbers).
 */
export function directionWords<Row>(
  column: SortColumn<Row>,
  dir: SortDir,
  rows: readonly Row[] = [],
): string {
  const at = dir === 'asc' ? 0 : 1;
  const own = column.sort?.words;
  if (own !== undefined) return own[at];
  let numbers = column.numeric === true;
  const value = column.sort?.value;
  if (value !== undefined)
    for (const row of rows) {
      const v = comparable(value(row));
      if (v === null) continue;
      numbers = typeof v === 'number';
      break;
    }
  return (numbers ? NUMBER_WORDS : TEXT_WORDS)[at];
}

/** The sort in words, for the status line and the card rows' menu: "Submitted, oldest first". */
export function sortLabel<Row>(
  columns: readonly SortColumn<Row>[],
  sort: TableSort,
  rows: readonly Row[] = [],
): string {
  const column = columns.find((c) => c.id === sort.column);
  if (column === undefined) return '';
  return `${column.header}, ${directionWords(column, sort.dir, rows)}`;
}
